import { useCallback, useEffect, useRef } from 'react';
import { AppState, AppStateStatus } from 'react-native';
import moment from 'moment';
import constants from '@/config/constants';
import { localLogoutUser } from '@/api/services/user';
import {
  clearLocalData,
  getAccessToken,
  serverLogoutUser,
} from '@/stores/auth/services';
import store, { useStoreState } from '@/stores/store';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';

export function useIdleLogout() {
  const { showNotification } = useNotification();
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const idleStartRef = useRef<number | null>(null);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  const { isAuthenticated, stayLoggedIn } = useStoreState((state) => ({
    isAuthenticated: state.isAuthenticated,
    stayLoggedIn: state.stayLoggedIn,
  }));
  const timeoutMs = moment
    .duration(constants.idleTimeToLogout, 'minutes')
    .asMilliseconds();

  const handleLogout = useCallback(async () => {
    showNotification({
      title: 'Sitzung abgelaufen. Bitte melden Sie sich neu an.',
      type: 'error',
    });
    await serverLogoutUser({ jwt: (await getAccessToken()) ?? '' });
    localLogoutUser();
    clearLocalData();
    store.getActions().setLastIdleDate(null);
    idleStartRef.current = null;
  }, [showNotification]);

  const resetTimerRef = useRef<() => void>(() => {});

  useEffect(() => {
    resetTimerRef.current = () => {
      if (!isAuthenticated || stayLoggedIn) {
        return;
      }

      idleStartRef.current = Date.now();
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleLogout, timeoutMs);
    };
  }, [isAuthenticated, stayLoggedIn, handleLogout, timeoutMs]);

  useEffect(() => {
    const handleAppStateChange = (nextState: AppStateStatus) => {
      const prevState = appStateRef.current;
      appStateRef.current = nextState;

      if (nextState === 'active' && prevState !== 'active') {
        if (!isAuthenticated || stayLoggedIn) {
          return;
        }

        if (idleStartRef.current !== null) {
          const elapsed = Date.now() - idleStartRef.current;
          if (elapsed >= timeoutMs) {
            clearTimeout(timerRef.current);
            handleLogout();
          } else {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(handleLogout, timeoutMs - elapsed);
          }
        }
      }
    };

    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange,
    );
    return () => subscription.remove();
  }, [isAuthenticated, stayLoggedIn, handleLogout, timeoutMs]);

  useEffect(() => {
    if (isAuthenticated && !stayLoggedIn) {
      resetTimerRef.current();
    } else {
      clearTimeout(timerRef.current);
      idleStartRef.current = null;
    }
    return () => clearTimeout(timerRef.current);
  }, [isAuthenticated, stayLoggedIn]);

  return { resetTimer: () => resetTimerRef.current() };
}
